import isEmpty from 'lodash/isEmpty';

import { MODULE_NAME } from './const';
import { usePricingPreviews, usePricingPreview, useEntitlements } from './getters';
import {
	SET_PRODUCTS,
	SET_ENTITLEMENTS,
	SET_CUSTOMER_DATA,
	SET_SUBSCRIPTIONS,
	SET_PAYMENT_METHODS,
	SET_PRICING_PREVIEW,
	SET_IS_ANNUALLY_PLAN,
	DELETE_PAYMENT_METHOD,
	SET_ENTITLEMENT_PLANS,
	SET_SUBSCRIPTION_HISTORY,
	SET_ORGANIZATION_SETTINGS,
	SET_FEATURE_USAGE_HISTORY,
	SET_FEATURE_USAGE_ANALYTICS,
	SET_ENTITLEMENTS_LAST_REFRESH,
	SET_DEFAULT_PAYMENT_METHOD_ID,
	SET_PRODUCT_AUTOMATED_CHANGES,
	SET_ENTITLEMENTS_IS_REFRESHING,
} from './mutations';
import StoreModuleService from '../StoreModuleService';
import { updateAccount } from '../account/actions';
import api from '@/api';
import BillingService from '@/components/Billing/billing-service';
import ErrorHandlingService, { STATUS_CODES, PlanPurchaseError } from '@/utils/error-handling-service';
import { arrayToObject } from '@/utils/helpers';
import pusherService, { PUSHER_USAGE_TYPES } from '@/utils/pusher';

const ACCOUNT_BILLING_UPDATED_EVENT_KEY = 'account-billing-updated';

const buildActionObject = (action) => StoreModuleService.buildActionObject(MODULE_NAME, action);

// actions declarations
export const getProducts = buildActionObject('getProducts');
export const clearProducts = buildActionObject('clearProducts');
export const getCustomerData = buildActionObject('getCustomerData');
export const setPaymentMethods = buildActionObject('setPaymentMethods');
export const setSubscriptions = buildActionObject('setSubscriptions');
export const setPaymentMethod = buildActionObject('setPaymentMethod');
export const getPaymentMethod = buildActionObject('getPaymentMethod');
export const getPaymentIntent = buildActionObject('getPaymentIntent');
export const setIsAnnuallyPlan = buildActionObject('setIsAnnuallyPlan');
export const getBillingPreview = buildActionObject('getBillingPreview');
export const clearEntitlements = buildActionObject('clearEntitlements');
export const setPricingPreview = buildActionObject('setPricingPreview');
export const clearPricePreviews = buildActionObject('clearPricePreviews');
export const clearSubscriptions = buildActionObject('clearSubscriptions');
export const subscribeToChanges = buildActionObject('subscribeToChanges');
export const upgradeBillingPlans = buildActionObject('upgradeBillingPlans');
export const getEntitlementPlans = buildActionObject('getEntitlementPlans');
export const clearEntitlementPlans = buildActionObject('clearEntitlementPlans');
export const cancelProductDowngrade = buildActionObject('cancelProductDowngrade');
export const getAccountSubscriptions = buildActionObject('getAccountSubscriptions');
export const setIsSmartUpgradeEnabled = buildActionObject('setIsSmartUpgradeEnabled');
// USAGE
export const getInvoices = buildActionObject('getInvoices');
export const getEntitlements = buildActionObject('getEntitlements');
export const deletePaymentMethod = buildActionObject('deletePaymentMethod');
export const getFeatureUsageHistory = buildActionObject('getFeatureUsageHistory');
export const getSubscriptionHistory = buildActionObject('getSubscriptionHistory');
export const getOrganizationSettings = buildActionObject('getOrganizationSettings');
export const setDefaultPaymentMethod = buildActionObject('setDefaultPaymentMethod');
export const setOrganizationSettings = buildActionObject('setOrganizationSettings');
export const saveBillingEmailsConfig = buildActionObject('saveBillingEmailsConfig');
export const saveOrganizationSettings = buildActionObject('saveOrganizationSettings');
export const purchaseSingleProduct = buildActionObject('purchaseSingleProduct');
export const requestFeatureAccess = buildActionObject('requestFeatureAccess');
export const getProductAutomatedSubscriptionChanges = buildActionObject('getProductAutomatedSubscriptionChanges');
const actions = {
	...StoreModuleService.createDefaultActions(),
	// eslint-disable-next-line no-empty-pattern
	async [requestFeatureAccess.action]({}, { hashId }) {
		return api.requestFeatureAccess(hashId);
	},
	async [getProductAutomatedSubscriptionChanges.action]({ commit }, { productId }) {
		const productAutomatedSubscriptionChanges = await api.getProductAutomatedSubscriptionChanges(productId);
		commit(SET_PRODUCT_AUTOMATED_CHANGES, { productId, productAutomatedSubscriptionChanges });
		return productAutomatedSubscriptionChanges;
	},
	async [saveOrganizationSettings.action]({ dispatch }, { organizationSettings }) {
		const organizationSettingsBody = await api.saveOrganizationSettings(organizationSettings);
		dispatch(setOrganizationSettings.action, { organizationSettingsBody });
	},
	async [purchaseSingleProduct.action]({ dispatch, commit }, { purchaseDetails }) {
		const { productType, planId, quantity } = purchaseDetails;
		const { subscriptions, entitlements } = await api.purchaseSingleProduct(productType, planId, quantity);
		dispatch(setSubscriptions.action, { subscriptions });
		commit(SET_ENTITLEMENTS, entitlements);
	},
	async [getFeatureUsageHistory.action]({ commit, dispatch }, { featureId }) {
		dispatch(getEntitlements.action, { forceGet: true });
		const [featureUsageHistory, featureUsageAnalytics] = await Promise.all([
			api.getFeatureUsageHistory(featureId),
			api.getUsageAnalytics(featureId),
		]);
		commit(SET_FEATURE_USAGE_ANALYTICS, { featureId, featureUsageAnalytics });
		commit(SET_FEATURE_USAGE_HISTORY, { featureId, featureUsageHistory });
	},
	async [getSubscriptionHistory.action]({ commit }, { productId }) {
		const subscriptionHistory = await api.getSubscriptionHistory(productId);
		commit(SET_SUBSCRIPTION_HISTORY, { productId, subscriptionHistory });
		return subscriptionHistory;
	},
	async [getOrganizationSettings.action]({ dispatch }) {
		try {
			const organizationSettingsBody = await api.getOrganizationSettings();
			dispatch(setOrganizationSettings.action, { organizationSettingsBody });
		} catch (error) {
			dispatch(setOrganizationSettings.action, { organizationSettingsBody: { address: {} } });
		}
	},
	async [setOrganizationSettings.action]({ commit }, { organizationSettingsBody }) {
		const address = organizationSettingsBody.address || {};
		const organizationSettings = {
			city: address.city || '',
			addressLine1: address.line1 || '',
			addressLine2: address.line2 || '',
			postalCode: address.postal_code || '',
			countryOrRegion: address.country || '',
			organizationName: organizationSettingsBody.name,
		};
		commit(SET_ORGANIZATION_SETTINGS, organizationSettings);
	},
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	async [saveBillingEmailsConfig.action]({ commit }, { billingEmailsConfig }) {
		return api.saveBillingEmailsConfig(billingEmailsConfig);
	},
	async [getEntitlements.action]({ commit, getters }, payload) {
		const entitlements = getters[useEntitlements.getter];
		const needToGet = isEmpty(entitlements);
		const isForce = payload?.forceGet;
		if (needToGet || isForce) {
			if (isForce) {
				commit(SET_ENTITLEMENTS_IS_REFRESHING, true);
			}
			try {
				const entitlements = await api.getEntitlements();
				commit(SET_ENTITLEMENTS, entitlements);
				commit(SET_ENTITLEMENTS_LAST_REFRESH, Date.now());
			} finally {
				commit(SET_ENTITLEMENTS_IS_REFRESHING, false);
			}
			return entitlements;
		} else {
			return entitlements;
		}
	},
	async [getInvoices.action]() {
		return api.getInvoices();
	},
	async [deletePaymentMethod.action]({ commit }, { paymentMethodId }) {
		await api.deletePaymentMethod(paymentMethodId);
		commit(DELETE_PAYMENT_METHOD, paymentMethodId);
	},

	async [getProducts.action]({ commit }) {
		const res = await api.getBillingProducts();
		commit(SET_PRODUCTS, res);
	},
	async [getCustomerData.action]({ commit }) {
		const res = await api.getCustomerData();
		commit(SET_CUSTOMER_DATA, res);
	},
	async [setSubscriptions.action]({ commit, dispatch }, { subscriptions }) {
		commit(SET_SUBSCRIPTIONS, arrayToObject(subscriptions));
		dispatch(clearPricePreviews.action);
	},
	async [upgradeBillingPlans.action]({ dispatch }, { purchaseDetails, customerInvoiceName }) {
		const { subscriptions, action_info: actionInfo } = await api.upgradeBillingPlans(purchaseDetails, customerInvoiceName);
		dispatch(setSubscriptions.action, { subscriptions });
		dispatch(getCustomerData.action);
		const actionInfoErrors = Object.values(actionInfo)
			.map((info) => info.error)
			.filter((error) => error);
		const didPurchaseFail = actionInfoErrors.length > 0;
		if (didPurchaseFail) {
			throw new PlanPurchaseError(JSON.stringify(actionInfo));
		}
	},
	async [setPaymentMethod.action]({ dispatch }, { paymentMethodId, name }) {
		const paymentMethods = await api.setPaymentMethod(paymentMethodId, name);
		dispatch(setPaymentMethods.action, { paymentMethods });
	},
	async [setPaymentMethods.action]({ commit }, { paymentMethods }) {
		commit(SET_PAYMENT_METHODS, arrayToObject(paymentMethods.payment_methods.results));
		commit(SET_DEFAULT_PAYMENT_METHOD_ID, paymentMethods.default_payment_method_id);
	},
	async [setDefaultPaymentMethod.action]({ dispatch }, { paymentMethodId }) {
		const paymentMethods = await api.setDefaultPaymentMethod(paymentMethodId);
		dispatch(setPaymentMethods.action, { paymentMethods });
	},
	async [getAccountSubscriptions.action]({ dispatch }) {
		const subscriptions = await api.getAccountSubscriptions();
		dispatch(setSubscriptions.action, { subscriptions });
	},
	async [getPaymentMethod.action]({ commit, dispatch }) {
		try {
			const paymentMethods = await api.getPaymentMethods();
			dispatch(setPaymentMethods.action, { paymentMethods });
		} catch (err) {
			if (ErrorHandlingService.extractStatusCode(err) === STATUS_CODES.NOT_FOUND) {
				commit(SET_PAYMENT_METHODS, []);
				commit(SET_DEFAULT_PAYMENT_METHOD_ID, null);
			} else {
				throw err;
			}
		}
	},
	async [getPaymentIntent.action]() {
		// this should always be used once, and get a new one when needed
		return api.getPaymentIntent();
	},
	async [setIsAnnuallyPlan.action]({ commit }, { isAnnuallyPlan }) {
		commit(SET_IS_ANNUALLY_PLAN, isAnnuallyPlan);
	},
	async [cancelProductDowngrade.action]({ dispatch }, { productType }) {
		const { subscriptions } = await api.cancelProductDowngrade(productType);
		dispatch(setSubscriptions.action, { subscriptions });
	},
	async [setIsSmartUpgradeEnabled.action]({ dispatch }, { isSmartUpgradeEnabled }) {
		await dispatch(updateAccount.fullPath, { is_smart_upgrade_enabled: isSmartUpgradeEnabled }, { root: true });
	},
	async [setPricingPreview.action]({ commit, getters }, { pricingPreview, pricePreviewInput }) {
		const pricingPreviewKey = BillingService.buildPricingPreviewKey(pricePreviewInput);
		const pricingPreviews = getters[usePricingPreviews.getter];
		const updatedPricingPreviews = { ...pricingPreviews, [pricingPreviewKey]: pricingPreview };
		commit(SET_PRICING_PREVIEW, updatedPricingPreviews);
	},
	async [getBillingPreview.action]({ getters, dispatch }, { pricePreviewInput, forceGet }) {
		const pricingPreviewInStore = getters[usePricingPreview.getter](pricePreviewInput);
		if (pricingPreviewInStore && !forceGet) {
			return pricingPreviewInStore;
		} else {
			const pricingPreview = await api.getBillingPreview(pricePreviewInput);
			dispatch(setPricingPreview.action, { pricingPreview, pricePreviewInput });
			return pricingPreview;
		}
	},
	async [clearPricePreviews.action]({ commit }) {
		commit(SET_PRICING_PREVIEW, {});
	},
	async [clearSubscriptions.action]({ commit }) {
		commit(SET_SUBSCRIPTIONS, {});
	},
	async [getEntitlementPlans.action]({ commit }) {
		const entitlementPlans = await api.getBillingEntitlementPlans();
		commit(SET_ENTITLEMENT_PLANS, entitlementPlans);
	},
	[subscribeToChanges.action]({ dispatch }) {
		pusherService.subscribeToAccount();
		// careful with this listener - lot of users are listening at once. we don't want to ddos ourselves
		pusherService.listenToEvents([
			pusherService.createPusherEventObj(
				ACCOUNT_BILLING_UPDATED_EVENT_KEY,
				() => {
					dispatch(clearPricePreviews.action);
					// TODO: this is a hack for now. we should clear all this data, and get on demand when needed. in big scale this will cause DDOS
					dispatch(getEntitlementPlans.action);
					dispatch(getAccountSubscriptions.action);
					dispatch(getEntitlements.action, { forceGet: true });
				}, // set flag or clear the data for entitlements and subscriptions "clear cache"
				PUSHER_USAGE_TYPES.ACCOUNT
			),
		]);
	},
	async [clearProducts.action]({ commit }) {
		commit(SET_PRODUCTS, {});
	},
	async [clearEntitlements.action]({ commit }) {
		commit(SET_ENTITLEMENTS, {});
	},
	async [clearEntitlementPlans.action]({ commit }) {
		commit(SET_ENTITLEMENT_PLANS, {});
	},
};

export default actions;
